/**
 * Stock recommendation engine — combines technical analysis + fundamentals
 * into a single 0..100 score, mapped to BUY / HOLD / SELL.
 *
 * Scoring weights:
 *   - Technical (55 weight): momentum, trend vs SMA20, RSI proxy, 52W position
 *   - Fundamental (45 weight): P/E vs median, EPS sign, dividend yield, volume
 *
 * Everything is pure and deterministic given the inputs, so the traffic-light
 * output for a given stock updates the instant any live value changes (price,
 * pctChange, volume, bid/ask, fundamentals via WS). No external API calls.
 *
 * Bucketing:
 *   score >= 65 -> BUY  (green)
 *   35..64      -> HOLD (amber)
 *   score < 35  -> SELL (red)
 */

export type Verdict = 'BUY' | 'HOLD' | 'SELL';

export interface StockSignalInput {
  symbol: string;
  price: number;
  pctChange: number;
  /** Optional live fields — when missing, seeded per-symbol fallbacks are used. */
  volume?: number;
  bid?: number; ask?: number;
  weekHigh52?: number; weekLow52?: number;
  pe?: number; eps?: number;
  yieldPct?: number;
  /** Optional recent closes used for SMA20 + RSI14 proxies. */
  recentCloses?: number[];
}

export interface StockSignalOut {
  score: number;     // 0..100
  verdict: Verdict;
  technical: number; // 0..100
  fundamental: number; // 0..100
  drivers: string[]; // human-readable reasons (top 4)
}

function clamp(v: number, lo = 0, hi = 100): number {
  return Math.max(lo, Math.min(hi, v));
}
function seed(symbol: string, salt: number): number {
  let h = 2166136261 >>> 0;
  for (let i = 0; i < symbol.length; i++) h = Math.imul(h ^ symbol.charCodeAt(i), 16777619) >>> 0;
  h = Math.imul(h ^ salt, 16777619) >>> 0;
  return h;
}
function seededNum(sym: string, salt: number, lo: number, hi: number): number {
  return lo + (seed(sym, salt) % 100000) / 100000 * (hi - lo);
}

// ---- Technical score ------------------------------------------------------

function technicalScore(i: StockSignalInput, drivers: string[]): number {
  let score = 50;

  // Momentum from pct change (cap at +-5% swings for scoring sensitivity).
  const mom = Math.max(-5, Math.min(5, i.pctChange));
  score += mom * 3;
  if (mom > 2.5) drivers.push('Strong intraday momentum (+' + mom.toFixed(2) + '%)');
  else if (mom < -2.5) drivers.push('Sharp intraday decline (' + mom.toFixed(2) + '%)');

  // 52-week positioning: 0 at low, 1 at high. Favor mid-to-upper band.
  const lo52 = i.weekLow52 ?? seededNum(i.symbol, 2, i.price * 0.6, i.price * 0.9);
  const hi52 = i.weekHigh52 ?? seededNum(i.symbol, 3, i.price * 1.05, i.price * 1.6);
  const pos52 = hi52 > lo52 ? (i.price - lo52) / (hi52 - lo52) : 0.5;
  if (pos52 > 0.8) { score += 10; drivers.push('Trading near 52-week highs'); }
  else if (pos52 > 0.55) { score += 5; }
  else if (pos52 < 0.2) { score -= 10; drivers.push('Near 52-week lows'); }
  else if (pos52 < 0.4) { score -= 4; }

  // SMA20 trend (if we have closes).
  const closes = i.recentCloses;
  if (closes && closes.length >= 5) {
    const n = Math.min(20, closes.length);
    const sma = closes.slice(-n).reduce((a, b) => a + b, 0) / n;
    const deviation = (i.price - sma) / sma;
    if (deviation > 0.02) { score += 6; drivers.push('Price above SMA20 (uptrend)'); }
    else if (deviation < -0.02) { score -= 6; drivers.push('Price below SMA20 (downtrend)'); }

    // RSI14 proxy: fraction of up days over last 14.
    if (closes.length >= 5) {
      const slice = closes.slice(-14);
      let up = 0, dn = 0;
      for (let k = 1; k < slice.length; k++) {
        const diff = slice[k]! - slice[k - 1]!;
        if (diff > 0) up += diff; else dn -= diff;
      }
      const rs = dn === 0 ? 100 : up / (up + dn) * 100;
      if (rs < 30) { score += 6; drivers.push('Oversold (RSI proxy ~' + rs.toFixed(0) + ')'); }
      else if (rs > 70) { score -= 6; drivers.push('Overbought (RSI proxy ~' + rs.toFixed(0) + ')'); }
    }
  }

  // Volume confirmation — above-average volume reinforces direction.
  const vol = i.volume ?? seededNum(i.symbol, 10, 80_000, 2_000_000);
  if (vol > 1_000_000) {
    score += i.pctChange >= 0 ? 3 : -3;
    if (i.pctChange >= 0) drivers.push('High-volume buying');
    else drivers.push('High-volume selling');
  }

  // Spread health — tight spread = liquid & healthy.
  if (i.bid && i.ask && i.ask > i.bid) {
    const spreadBps = ((i.ask - i.bid) / i.price) * 10000;
    if (spreadBps > 50) score -= 2; // wide spread => slight caution
  }

  return clamp(score);
}

// ---- Fundamental score ---------------------------------------------------

function fundamentalScore(i: StockSignalInput, drivers: string[]): number {
  let score = 50;

  const pe = i.pe ?? seededNum(i.symbol, 16, 4, 42);
  const eps = i.eps ?? seededNum(i.symbol, 14, -2, 18);
  const y = i.yieldPct ?? seededNum(i.symbol, 19, 0, 12);

  // P/E: below 10 = cheap, 10..20 = fair, 20..30 = rich, >30 = stretched
  if (pe > 0 && pe < 10) { score += 10; drivers.push('P/E ' + pe.toFixed(1) + ' (value)'); }
  else if (pe <= 18) { score += 3; }
  else if (pe <= 26) { score -= 0; }
  else if (pe <= 35) { score -= 6; drivers.push('P/E ' + pe.toFixed(1) + ' (rich)'); }
  else { score -= 12; drivers.push('P/E ' + pe.toFixed(1) + ' (stretched)'); }

  // EPS sign + magnitude.
  if (eps > 5) { score += 8; drivers.push('Strong EPS (' + eps.toFixed(2) + ')'); }
  else if (eps > 0) { score += 3; }
  else if (eps > -1) { score -= 4; }
  else { score -= 10; drivers.push('Negative EPS (' + eps.toFixed(2) + ')'); }

  // Dividend yield.
  if (y > 7) { score += 6; drivers.push('High yield (' + y.toFixed(1) + '%)'); }
  else if (y > 3) { score += 3; }

  return clamp(score);
}

export function scoreStock(i: StockSignalInput): StockSignalOut {
  const drivers: string[] = [];
  const technical = technicalScore(i, drivers);
  const fundamental = fundamentalScore(i, drivers);
  const score = Math.round(technical * 0.55 + fundamental * 0.45);
  const verdict: Verdict = score >= 65 ? 'BUY' : score < 35 ? 'SELL' : 'HOLD';
  // Keep top 4 drivers, trim rest.
  return { score, verdict, technical: Math.round(technical), fundamental: Math.round(fundamental), drivers: drivers.slice(0, 4) };
}
